import { APP_ID, Injectable } from '@angular/core';
import { HTTPWrapperContext, RequestConfigImplementation, REQUEST_METHODS, XHR_STRATEGIES } from '@ptl/xhr';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { BrandingConfigurationService } from '@ptl/brand';
import { HttpHeaders } from '@angular/common/http';
import { DateService } from 'src/app/shared/services/date.service';
import { planDetailsByPlanId } from 'src/app/angular-elements/plan-selection/configs/reducers/plan-details.reducer';
import { PRINT_TYPE } from '../enums/print-type.enum';
import { EnrollmentAppStateStructure } from '../../../interfaces/enrollmentAppStateStructure.interface';
import { APPLICANT_RELATION } from 'src/app/angular-elements/plan-selection/services/interfaces/shared/plan-demographics/applicant.interface';


@Injectable({
  providedIn: 'root'
})
export class PrintButtonService {

  constructor(
    private httpClient: HTTPWrapperContext,
    private reducerUtilityService: ReducerUtilityService,
    private brandService: BrandingConfigurationService,
    private date: DateService
  ) { }

  //exchange this function for new service using power pdf
  fetchSEPApplicationPDF(appId: string) {
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.GET,
      contentType: 'application/octet-stream',
      accept: 'application/octet-stream',
      responseType: 'blob',
      url: '/sxesvc/api/v3/pdf/downloadSepForm',
      GET_PAYLOAD: {
        appTrackingId: appId
      }
    });
    
    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS);
    return this.httpClient.makeRequest(requestConfig);
  }

  saveByteArray(name: string, byte) {
    let blob = new Blob([byte], { type: "application/pdf" });
    let link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    let fileName = name;
    link.download = fileName;
    link.click();
  }
  
  // Modified fetchSubmittedApplicationPDF method to support Dental applications with region
  async fetchSubmittedApplicationPDF(printConfig: {
    appId: string,
    region?: string,
    productGroup?: string,
    subGroup: PRINT_TYPE
  }) {
    const payload: {
      applicationId: string;
      region?: string;
      productGroup?: string;
      subGroup: PRINT_TYPE;
    } = {
      applicationId: printConfig.appId,
      subGroup: printConfig.subGroup
    };
    
    // Add region and productGroup to payload if provided (for Dental applications)
    if (printConfig.region) {
      payload.region = printConfig.region;
    }
    
    if (printConfig.productGroup) {
      payload.productGroup = printConfig.productGroup;
    }
    
    
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.GET,
      url: `/sxesvc/api/v3/generate-pdf/generatePaperApp`,
      accept: 'application/octet-stream',
      contentType: 'application/json',
      responseType: 'blob',
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded(),
      }),
      GET_PAYLOAD: payload
    });
    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_PRE_PASS);
    return this.httpClient.makeRequest(requestConfig);
  }

  async downloadSEPApplicationPDF(appId: string) {
    const fileBytes = await this.fetchSEPApplicationPDF(appId);
    this.saveByteArray(`${appId}-Documentation`, fileBytes);
  }

  async fetchPlanDetailsPDF(planDetailValues){
    const planProductGroup = this.reducerUtilityService.getComponentStates().Plans.activeProductGroup;
    const planIdsArray = planDetailValues.lastQueriedPlanIds;
    let planTemplateName = planDetailValues.individualPlanIdForPlanDetails ? "PLAN_BENEFITS" : "COMPARE_PLANS_"+(planIdsArray.length);
    let planID: number = 0;
    let basePath = null;
    const region = this.brandService.getCurrentBrand().region;
    const planBenefitsPayload = {
      "baseMessage": {
        "headerVersion": "1.0"
      },
      "payload": {
        "template": planTemplateName,
        "region": region,
        "keyValues": []
        }
      }

    for(let i = 0; i < planIdsArray.length; i++){
      planID = planIdsArray[i]
      basePath = planDetailValues.planDetails[planProductGroup][planID];
      planBenefitsPayload.payload.keyValues.push(...this.generatePrintPlanPayload(basePath,(i+1)));
    }

    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: `/sxesvc/api/v3/generate-pdf/generate`,
      accept: 'application/octet-stream',
      contentType: 'application/json',
      responseType: 'blob',
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded(),
      }),
      POST_OR_PUT_PAYLOAD: planBenefitsPayload,
      });

      this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_PRE_PASS);
      return this.httpClient.makeRequest(requestConfig);
  }

  async fetchFamilyPremiumPDF(familyPremiumDetails){
    
    const region = this.brandService.getCurrentBrand().region;
    const planProductGroup = this.reducerUtilityService.getComponentStates().Plans.activeProductGroup;
    const planID = await this.reducerUtilityService.getComponentStates().PlanDetails.lastQueriedPlanIds[0];
    const familyPremiumPath = familyPremiumDetails.planDetails[planProductGroup][planID];
    let familyPremiumApplicants = familyPremiumPath?.rateDetails?.applicants;

    const familyPremiumPayload = {
      "baseMessage": {
        "headerVersion": "1.0"
      },
      "payload": {
        "template": "FAMILY_PREMIUM_"+ familyPremiumApplicants.length,
        "region": region,
        "keyValues": [
            {
              "key": "PLAN_NAME",
              "value": familyPremiumPath.productName,
              "checkBox": false
            },
            {
              "key": "MONTHLY_FAMILY_PREMIUM",
              "value": familyPremiumPath.price,
              "checkBox": false
            },
            {
              "key": "EST_ANNUAL_PREMIUM",
              "value": (Number(familyPremiumPath.price) * 12).toFixed(2),
              "checkBox": false
            },
            {
              "key": "COINSURANCE",
              "value": familyPremiumPath.summaryView.benefits[0].value,
              "checkBox": false
            },
            {
              "key": "OOP_MAX",
              "value": familyPremiumPath.summaryView.benefits[1].value,
              "checkBox": false
            },
            {
              "key": "DEDUCTIBLE",
              "value": familyPremiumPath.summaryView.benefits[2].value,
              "checkBox": false
            },
          ]
        }
      }

    const subscriberApplicant = familyPremiumApplicants.find(applicant => applicant.relationship === "Subscriber");
    if(subscriberApplicant){
      familyPremiumPayload.payload.keyValues.push(...this.createApplicant(subscriberApplicant, 1));
    }
    
    const partnerApplicant =  familyPremiumApplicants.find(applicant => applicant.relationship.includes("Spouse") === true) ;
    if(partnerApplicant){
      familyPremiumPayload.payload.keyValues.push(...this.createApplicant(partnerApplicant, 2));
    }
    
    let applicantNum = partnerApplicant && subscriberApplicant ? 3 : (subscriberApplicant && !partnerApplicant ? 2 : 1);
    const depArray =  familyPremiumApplicants.filter(applicant => applicant.relationship === "Dependent")

    if(depArray){
      depArray.sort((depA, depB) => (depA.personId ?? 0) - (depB.personId ?? 0));
      for(let i = 0; i < depArray.length; i++){
        familyPremiumPayload.payload.keyValues.push(...this.createApplicant(depArray[i], applicantNum));
        applicantNum++;
      }
    }

    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: `/sxesvc/api/v3/generate-pdf/generate`,
      accept: 'application/octet-stream',
      contentType: 'application/json',
      responseType: 'blob',
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded(),
      }),
      POST_OR_PUT_PAYLOAD: familyPremiumPayload,
      });
  
      this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_PRE_PASS);
      return this.httpClient.makeRequest(requestConfig);
  }


  tobaccoDisplay(value) {
    switch (value) {
      case 'N':
        return 'Non Tobacco User';
      case 'Y':
        return 'Tobacco User';
      default:
        return '';
    }
  }

  createApplicant(applicant: any, applicantNumber: number){
    const applicantPayload = 
        [{
          "key": "APPLICANT_"+(applicantNumber)+"_RELATIONSHIP",
          "value": applicant.relationship,
          "checkBox": false
        },
        {
          "key": "APPLICANT_"+(applicantNumber)+"_RATE",
          "value": applicant.rate,
          "checkBox": false
        },          {
          "key": "APPLICANT_"+(applicantNumber)+"_DOB",
          "value": this.formatDate(applicant.dob),
          "checkBox": false
        },          {
          "key": "APPLICANT_"+(applicantNumber)+"_TOBACCO_STATUS",
          "value": this.tobaccoDisplay(applicant.tobaccoStatus),
          "checkBox": false
        }]

    return applicantPayload
  }

  generatePrintPlanPayload(basePath, planNumber){
    const basicPlanCostsPath = basePath.detailView.benefitGrid[0].masterCategory;
    const healthBenefitsPath = basePath.detailView.benefitGrid[1].masterCategory;
    const prescriptionDrugsPath = basePath.detailView.benefitGrid[2].masterCategory;

    const planPayload = 
      [{
          "key": "PLAN_NAME_"+planNumber,
          "value": basePath.productName,
          "checkBox": false
      },
      {
        "key": "MONTHLY_PREMIUM_"+planNumber,
        "value": basePath.price,
        "checkBox": false
      },
      {
          "key": "PREVENTATIVE_CARE_INN_"+planNumber,
          "value": basicPlanCostsPath.categories[0].attributes[0].attributeContent["In-Network"],
          "checkBox": false
      },
      {
        "key": "PREVENTATIVE_CARE_OON_"+planNumber,
        "value": basicPlanCostsPath.categories[0].attributes[0].attributeContent["Out-of-Network"],
        "checkBox": false
      },
      {
        "key": "COINSURANCE_INN_"+planNumber,
        "value": basicPlanCostsPath.categories[1].attributes[0].attributeContent["In-Network"],
        "checkBox": false
      },            
      {
        "key": "COINSURANCE_OON_"+planNumber,
        "value": basicPlanCostsPath.categories[1].attributes[0].attributeContent["Out-of-Network"],
        "checkBox": false
      },
      {
        "key": "OUT_OF_POCKET_MAX_FAMILY_INN_"+planNumber,
        "value": basicPlanCostsPath.categories[1].attributes[1].attributeContent["In-Network"],
        "checkBox": false
      },
      {
        "key": "OUT_OF_POCKET_MAX_FAMILY_OON_"+planNumber,
        "value": basicPlanCostsPath.categories[1].attributes[1].attributeContent["Out-of-Network"],
        "checkBox": false
      },
      {
        "key": "OUT_OF_POCKET_MAX_INDIVIDUAL_INN_"+planNumber,
        "value": basicPlanCostsPath.categories[1].attributes[2].attributeContent["In-Network"],
        "checkBox": false
      },
      {
        "key": "OUT_OF_POCKET_MAX_INDIVIDUAL_OON_"+planNumber,
        "value": basicPlanCostsPath.categories[1].attributes[2].attributeContent["Out-of-Network"],
        "checkBox": false
      },
      {
        "key": "DEDUCTIBLE_INDIVIDUAL_INN_"+planNumber,
        "value": basicPlanCostsPath.categories[1].attributes[3].attributeContent["In-Network"],
        "checkBox": false
      },
      {
        "key": "DEDUCTIBLE_INDIVIDUAL_OON_"+planNumber,
        "value": basicPlanCostsPath.categories[1].attributes[3].attributeContent["Out-of-Network"],
        "checkBox": false
      },
      {
        "key": "DEDUCTIBLE_FAMILY_INN_"+planNumber,
        "value": basicPlanCostsPath.categories[1].attributes[4].attributeContent["In-Network"],
        "checkBox": false
      },
      {
        "key": "DEDUCTIBLE_FAMILY_OON_"+planNumber,
        "value": basicPlanCostsPath.categories[1].attributes[4].attributeContent["Out-of-Network"],
        "checkBox": false
      },
      {
        "key": "PCP_OFFICE_VISITS_INN_"+planNumber,
        "value": healthBenefitsPath.categories[0].attributes[0].attributeContent["In-Network"],
        "checkBox": false
      },
      {
        "key": "PCP_OFFICE_VISITS_OON_"+planNumber,
        "value": healthBenefitsPath.categories[0].attributes[0].attributeContent["Out-of-Network"],
        "checkBox": false
      },
      {
        "key": "SP_OFFICE_VISITS_INN_"+planNumber,
        "value": healthBenefitsPath.categories[0].attributes[1].attributeContent["In-Network"],
        "checkBox": false
      },
      {
        "key": "SP_OFFICE_VISITS_OON_"+planNumber,
        "value": healthBenefitsPath.categories[0].attributes[1].attributeContent["Out-of-Network"],
        "checkBox": false
      },
      {
        "key": "HOSPITAL_INPATIENT_INN_"+planNumber,
        "value": healthBenefitsPath.categories[1].attributes[0].attributeContent["In-Network"],
        "checkBox": false
      },
      {
        "key": "HOSPITAL_INPATIENT_OON_"+planNumber,
        "value": healthBenefitsPath.categories[1].attributes[0].attributeContent["Out-of-Network"],
        "checkBox": false
      },
      {
        "key": "HOSPITAL_OUTPATIENT_INN_"+planNumber,
        "value": healthBenefitsPath.categories[1].attributes[1].attributeContent["In-Network"],
        "checkBox": false
      },
      {
        "key": "HOSPITAL_OUTPATIENT_OON_"+planNumber,
        "value": healthBenefitsPath.categories[1].attributes[1].attributeContent["Out-of-Network"],
        "checkBox": false
      },
      {
        "key": "EMERGENCY_SERVICES_INN_"+planNumber,
        "value": healthBenefitsPath.categories[2].attributes[0].attributeContent["In-Network"],
        "checkBox": false
      },
      {
        "key": "EMERGENCY_SERVICES_OON_"+planNumber,
        "value": healthBenefitsPath.categories[2].attributes[0].attributeContent["Out-of-Network"],
        "checkBox": false
      },
      {
        "key": "DIAGNOSTIC_IMAGING_INN_"+planNumber,
        "value": healthBenefitsPath.categories[3].attributes[0].attributeContent["In-Network"],
        "checkBox": false
      },
      {
        "key": "DIAGNOSTIC_IMAGING_OON_"+planNumber,
        "value": healthBenefitsPath.categories[3].attributes[0].attributeContent["Out-of-Network"],
        "checkBox": false
      },
      {
        "key": "DURABLE_MED_INN_"+planNumber,
        "value": healthBenefitsPath.categories[3].attributes[1].attributeContent["In-Network"],
        "checkBox": false
      },
      {
        "key": "DURABLE_MED_OON_"+planNumber,
        "value": healthBenefitsPath.categories[3].attributes[1].attributeContent["Out-of-Network"],
        "checkBox": false
      },
      {
        "key": "RETAIL_TIER_1_INN_"+planNumber,
        "value": prescriptionDrugsPath.categories[0].attributes[0].attributeContent["Tier 1"],
        "checkBox": false
      },
      {
        "key": "RETAIL_TIER_2_INN_"+planNumber,
        "value": prescriptionDrugsPath.categories[0].attributes[0].attributeContent["Tier 2"],
        "checkBox": false
      },
      {
        "key": "RETAIL_TIER_3_INN_"+planNumber,
        "value": prescriptionDrugsPath.categories[0].attributes[0].attributeContent["Tier 3"],
        "checkBox": false
      },
      {
        "key": "RETAIL_TIER_4_INN_"+planNumber,
        "value": prescriptionDrugsPath.categories[0].attributes[0].attributeContent["Tier 4 (Specialty drugs)"],
        "checkBox": false
      },
      {
        "key": "TIER_1_OON_"+planNumber,
        "value": prescriptionDrugsPath.categories[0].attributes[1].attributeContent["Tier 1"],
        "checkBox": false
      },
      {
        "key": "TIER_2_OON_"+planNumber,
        "value": prescriptionDrugsPath.categories[0].attributes[1].attributeContent["Tier 2"],
        "checkBox": false
      },
      {
        "key": "TIER_3_OON_"+planNumber,
        "value": prescriptionDrugsPath.categories[0].attributes[1].attributeContent["Tier 3"],
        "checkBox": false
      },
      {
        "key": "TIER_4_OON_"+planNumber,
        "value": prescriptionDrugsPath.categories[0].attributes[1].attributeContent["Tier 4 (Specialty drugs)"],
        "checkBox": false
      }]
    return planPayload;
  }

  formatDate(dob: string) {
    // Because the DOB values are saved on an EST server ( and therefore saved with ms in EST )
    // The values come back acceptable only for EST experiences
    // We are offsetting by 1-additional hour to align with CST
    const planRatesGuideDateOffset = 3600000; // 1 hour
    const dobWithOffset = dob + planRatesGuideDateOffset;
    return this.date.getDateAsFormattedString(dobWithOffset, 'MM-DD-YYYY');
  }

    getEnrollmentAppInfo(){

      let eApp: EnrollmentAppStateStructure = this.reducerUtilityService.getState('EnrollmentApp');
  
      if (!eApp.appId) {
        eApp = JSON.parse(sessionStorage?.getItem('confirmationBackup'));
        
      }

      return eApp;
  
    }
  
    async triggerPrintPdf(eApp: EnrollmentAppStateStructure, printType: PRINT_TYPE): Promise<void> {    
      const region = eApp.submittedPlan?.attributes?.PLAN_REGION?.[0];
      const productGroup = eApp.submittedPlan?.attributes?.PRODUCT_GROUP;
          
      // Fetch the PDF with region and product group information
      const response = await this.fetchSubmittedApplicationPDF({
        appId: eApp.appId,
        region: region,
        productGroup: productGroup,
        subGroup: printType
      });
      
      this.openPdfInPrintWindow(response);
    }
  
    openPdfInPrintWindow(blobData){
      let blob = new Blob([blobData], {type: "application/pdf"});
      const blobUrl = window.URL.createObjectURL(blob);
      // Open a new window with the Blob URL
      window.open(blobUrl, '_blank');
    }

}